/**
 * The wrist palette — the editor's VR tools, Horizon-style.
 *
 * A small panel that rides just above your left hand (or left controller),
 * facing you. Poke a tile with your right index finger, or point a ray and
 * pull the trigger / pinch.
 *
 *   Shapes — Library & Studio, box / sphere / cylinder / cone / torus / plane,
 *            portal, games, light: each one appears in your right hand
 *            (VRHeldItem) — release to drop it
 *   Edit   — move / turn / size mode, copy (into your hand), delete, snap,
 *            undo / redo (broadcast + saved, unlike a bare zundo undo)
 *   Colour — swatches and materials for the selected shape; "More materials"
 *            opens the Materials panel (AmbientCG PBR) attached above the palette
 *   World  — save, spawn here, back to start, screens, exit VR
 *
 * Opens and closes from the wrist bar's menu button (back of the left wrist)
 * or A / X on a controller. Replaces the old floating editor menu.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFrame, useThree, type ThreeEvent } from '@react-three/fiber';
import { useXR } from '@react-three/xr';
import { Text } from '@react-three/drei';
import * as THREE from 'three';
import { useWorldStore, type PrimitiveShape } from '../../../stores/worldStore';
import { broadcastObjectDelete, broadcastObjectUpdate } from '../../../services/worldBroadcast';
import { VR_MENU_EVENT } from '../hands/VRHandControls';
import { hands, handMode } from '../hands/handTracking';
import { vrColors as colors, noRaycast, Surface } from '../vrUi';
import { gameList, armGamePlacement } from '../../games/registry';
import { armArcadePlacement, armBoardPlacement, useArcadeGames, DEFAULT_BOARD } from '../../games/arcade/arcadeShared';
import { useVRBuild, type HeldItem } from './vrBuildState';
import { vrRedo, vrUndo } from './vrPlace';
import { applyAmbientCG, useAmbientCG, type MaterialAsset } from './ambientcg';

type Tab = 'shapes' | 'edit' | 'colour' | 'world';

const PW = .32;   // palette width (m)
const PH = .245;  // palette height (m)
const LIFT = .17; // how far above the hand it floats
const DEBOUNCE_MS = 350;

const SHAPES: { label: string; value: PrimitiveShape }[] = [
  { label: 'Box', value: 'box' }, { label: 'Sphere', value: 'sphere' }, { label: 'Cylinder', value: 'cylinder' },
  { label: 'Cone', value: 'cone' }, { label: 'Torus', value: 'torus' }, { label: 'Plane', value: 'plane' },
  { label: 'Portal', value: 'portal' },
];
const COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899', '#ffffff', '#9ca3af', '#1f2937'];
const MATERIALS: [string, string][] = [['wood1', 'Wood'], ['brick', 'Brick'], ['marble', 'Marble'], ['tile1', 'Tile'], ['grass', 'Grass'], ['cobblestone', 'Cobble'], ['sand', 'Sand'], ['dirt', 'Dirt']];

let lastPress = 0;
function pressOnce(fn: () => void) {
  const now = performance.now();
  if (now - lastPress < DEBOUNCE_MS) return;
  lastPress = now;
  fn();
}
const stop = (e: ThreeEvent<PointerEvent | MouseEvent>) => e.stopPropagation();

interface Rect { x: number; y: number; w: number; h: number; onPress: () => void; disabled?: boolean }

/** Small rounded tile with an optional glyph; registers itself for fingertip pokes. */
function Tile({ id, x, y, w, h, label, hint, onPress, reg, hot, active, primary, danger, disabled, swatch, glyph }: {
  id: string; x: number; y: number; w: number; h: number; label?: string; hint?: string; onPress: () => void;
  reg: (id: string, r: Rect) => void; hot: string | null; active?: boolean; primary?: boolean; danger?: boolean; disabled?: boolean;
  swatch?: string; glyph?: PrimitiveShape;
}) {
  reg(id, { x, y, w, h, onPress, disabled });
  const [hover, setHover] = useState(false);
  const lit = (hover || hot === id) && !disabled;
  const bg = disabled ? '#141e28' : primary ? (lit ? '#5ff0e3' : colors.teal) : lit ? colors.hover : active ? '#124940' : colors.card;
  const fg = disabled ? colors.muted : primary ? '#04211e' : danger ? '#ffb1ad' : colors.text;
  return <group position={[x, y, .004]} name={`vr-palette-${id}`}
    onPointerOver={(e) => { e.stopPropagation(); setHover(true); }} onPointerOut={() => setHover(false)}
    onPointerDown={stop} onClick={(e) => { e.stopPropagation(); if (!disabled) pressOnce(onPress); }}>
    <Surface width={w} height={h} color={swatch ? (lit ? colors.hover : colors.card) : bg} r={.008} />
    {swatch && <mesh raycast={noRaycast} position={[0, 0, .004]}><circleGeometry args={[Math.min(w, h) * .32, 20]} /><meshBasicMaterial color={swatch} toneMapped={false} /></mesh>}
    {glyph && <group position={[0, label ? .008 : 0, .012]}><Glyph shape={glyph} /></group>}
    {label && <Text raycast={noRaycast} position={[0, glyph ? -h / 2 + .011 : hint ? .006 : 0, .004]} fontSize={glyph ? .0092 : .0112} color={fg}
      maxWidth={w - .008} textAlign="center" anchorX="center" anchorY="middle">{label}</Text>}
    {hint && !glyph && <Text raycast={noRaycast} position={[0, -.0095, .004]} fontSize={.0074} color={active ? '#71e2cb' : colors.muted}
      maxWidth={w - .008} textAlign="center" anchorX="center" anchorY="middle">{hint}</Text>}
  </group>;
}

function Glyph({ shape }: { shape: PrimitiveShape }) {
  const ref = useRef<THREE.Mesh>(null);
  useFrame((_, dt) => { if (ref.current) ref.current.rotation.y += dt * .7; });
  const geom = useMemo(() => {
    const k = .0105;
    switch (shape) {
      case 'sphere': return new THREE.SphereGeometry(k, 16, 12);
      case 'cylinder': return new THREE.CylinderGeometry(k * .8, k * .8, k * 1.8, 18);
      case 'cone': return new THREE.ConeGeometry(k, k * 1.9, 18);
      case 'torus': return new THREE.TorusGeometry(k * .8, k * .3, 8, 20);
      case 'plane': return new THREE.BoxGeometry(k * 2, k * .2, k * 2);
      case 'portal': return new THREE.TorusGeometry(k * .9, k * .18, 8, 24);
      default: return new THREE.BoxGeometry(k * 1.6, k * 1.6, k * 1.6);
    }
  }, [shape]);
  useEffect(() => () => geom.dispose(), [geom]);
  return <mesh ref={ref} geometry={geom} raycast={noRaycast} rotation={[.4, 0, 0]}>
    <meshStandardMaterial color={shape === 'portal' ? '#a78bfa' : '#9fdcd6'} roughness={.4} metalness={.1} />
  </mesh>;
}

/** A material thumbnail tile (pokeable like the others). */
function ThumbTile({ id, x, y, w, h, url, label, busy, reg, hot, onPress }: {
  id: string; x: number; y: number; w: number; h: number; url: string; label: string; busy: boolean;
  reg: (id: string, r: Rect) => void; hot: string | null; onPress: () => void;
}) {
  reg(id, { x, y, w, h, onPress });
  const [hover, setHover] = useState(false);
  const [tex, setTex] = useState<THREE.Texture | null>(null);
  useEffect(() => {
    if (!url) return;
    let dead = false; let t: THREE.Texture | null = null;
    const img = new Image(); img.crossOrigin = 'anonymous';
    img.onload = () => { if (dead) return; t = new THREE.Texture(img); t.colorSpace = THREE.SRGBColorSpace; t.needsUpdate = true; setTex(t); };
    img.src = url;
    return () => { dead = true; t?.dispose(); };
  }, [url]);
  const lit = hover || hot === id;
  const imgH = h - .014;
  return <group position={[x, y, .004]} name={`vr-palette-${id}`}
    onPointerOver={(e) => { e.stopPropagation(); setHover(true); }} onPointerOut={() => setHover(false)}
    onPointerDown={stop} onClick={(e) => { e.stopPropagation(); pressOnce(onPress); }}>
    <Surface width={w} height={h} color={lit ? colors.teal : colors.card} r={.006} />
    <mesh raycast={noRaycast} position={[0, .006, .003]}>
      <planeGeometry args={[w - .008, imgH - .006]} />
      {/* keyed: a material that gains a map must be rebuilt, or it keeps rendering without it */}
      {tex ? <meshBasicMaterial key="map" map={tex} toneMapped={false} /> : <meshBasicMaterial key="blank" color="#1a2838" />}
    </mesh>
    <Text raycast={noRaycast} position={[0, -h / 2 + .006, .004]} fontSize={.0076} color={busy ? colors.teal : lit ? '#04211e' : colors.muted}
      maxWidth={w - .006} anchorX="center" anchorY="middle">{busy ? 'Applying…' : label}</Text>
  </group>;
}

const MAT_COLS = 4;
const MAT_ROWS = 3;
const MAT_H = .3; // Materials panel height (m), sits on top of the palette

const heldLabel = (h: HeldItem) => h.kind === 'light' ? 'Light' : h.kind === 'content' ? (h.template ? 'Copy' : 'Model')
  : h.shape === 'gametable' ? 'Game table' : h.shape === 'arcade' ? 'Arcade cabinet' : h.shape === 'leaderboard' ? 'Leaderboard' : h.template ? `${h.template.name ?? 'Copy'}` : h.shape.charAt(0).toUpperCase() + h.shape.slice(1);

const _p = new THREE.Vector3();
const _e = new THREE.Vector3();
const _m = new THREE.Matrix4();
const _tip = new THREE.Vector3();

export default function VRBuildPalette({ worldId, originRef, onOpenTextures }: {
  worldId: string;
  originRef: React.RefObject<THREE.Object3D | null>;
  onOpenTextures?: () => void;
}) {
  const session = useXR((s) => s.session);
  const canBuild = useWorldStore((s) => s.canBuild);
  const selectedId = useWorldStore((s) => s.selectedObjectId);
  const snapOn = useWorldStore((s) => s.snapSettings.grid);
  const activeTool = useWorldStore((s) => s.activeTool);
  const primitives = useWorldStore((s) => s.primitives);
  const setVrBuildActive = useWorldStore((s) => s.setVrBuildActive);
  const held = useVRBuild((s) => s.held);
  const { camera, gl } = useThree();
  const groupRef = useRef<THREE.Group>(null);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('shapes');
  const [gamesOpen, setGamesOpen] = useState(false);
  const arcade = useArcadeGames(gamesOpen);
  const [mode, setMode] = useState<string>(() => (window as any).__vrTransformMode || 'translate');
  const [notice, setNotice] = useState<string | null>(null);
  const [hot, setHot] = useState<string | null>(null);
  const [matsOpen, setMatsOpen] = useState(false);
  const [tile, setTile] = useState(4);
  const [applying, setApplying] = useState<string | null>(null);
  const mats = useAmbientCG(matsOpen && open, MAT_COLS * MAT_ROWS);
  const rects = useRef(new Map<string, Rect>());
  const pokeDown = useRef(false);
  const btnHeld = useRef(false);
  const placed = useRef(false);

  // A fresh registry every render: only tiles drawn right now can be poked.
  rects.current = new Map();
  const reg = useCallback((id: string, r: Rect) => { rects.current.set(id, r); }, []);

  const lastToggle = useRef(0);
  const toggle = useCallback(() => {
    const now = performance.now();
    if (now - lastToggle.current < 400) return;
    lastToggle.current = now;
    setOpen((v) => !v);
  }, []);

  useEffect(() => {
    setVrBuildActive(!!session && canBuild && (open || !!held || activeTool === 'place'));
    return () => setVrBuildActive(false);
  }, [session, canBuild, open, held, activeTool, setVrBuildActive]);
  useEffect(() => {
    window.addEventListener(VR_MENU_EVENT, toggle);
    return () => window.removeEventListener(VR_MENU_EVENT, toggle);
  }, [toggle]);
  useEffect(() => { if (!notice) return; const t = setTimeout(() => setNotice(null), 2200); return () => clearTimeout(t); }, [notice]);
  useEffect(() => { if (!open || held) setMatsOpen(false); }, [open, held]);
  // A model picked in Library & Studio goes into your hand, like a shape.
  useEffect(() => useVRBuild.subscribe((s) => {
    if (s.placing) { const spec = s.placing; s.setPlacing(null); s.setHeld({ kind: 'content', spec }); }
  }), []);

  useFrame((_st, _dt, frame?: XRFrame) => {
    // A / X on a controller toggles (never the system / Meta button).
    if (session) {
      let pressed = false;
      for (const src of session.inputSources) if (src.gamepad?.buttons?.[4]?.pressed) { pressed = true; break; }
      if (pressed && !btnHeld.current) toggle();
      btnHeld.current = pressed;
    }
    const g = groupRef.current; const origin = originRef.current;
    if (!g || !open || !origin) { placed.current = false; return; }
    origin.updateMatrixWorld();

    // ---- Anchor: just above the left hand / left controller ----
    let found = false;
    if (handMode.active && hands.left.present) {
      _p.copy(hands.left.wrist).lerp(hands.left.palm, .6).applyMatrix4(origin.matrixWorld);
      found = true;
    } else if (frame) {
      const ref = gl.xr.getReferenceSpace();
      for (const src of session!.inputSources) {
        if (src.handedness !== 'left' || src.hand || !src.gripSpace || !ref) continue;
        const pose = frame.getPose(src.gripSpace, ref);
        if (!pose) continue;
        _m.fromArray(pose.transform.matrix).premultiply(origin.matrixWorld);
        _p.setFromMatrixPosition(_m);
        found = true;
        break;
      }
    }
    if (found) {
      _p.y += LIFT;
      camera.getWorldPosition(_e);
      // Lean a little toward your eyes so it isn't hidden behind the hand.
      _p.lerp(_e, .12);
      if (!placed.current) { g.position.copy(_p); placed.current = true; } else g.position.lerp(_p, .35);
      g.lookAt(_e);
    }
    g.updateMatrixWorld();

    // ---- Fingertip poke (right index) ----
    let hovering: string | null = null;
    if (handMode.active && hands.right.present) {
      _tip.copy(hands.right.indexTip).applyMatrix4(origin.matrixWorld);
      g.worldToLocal(_tip);
      let hit: [string, Rect] | null = null;
      for (const entry of rects.current) {
        const r = entry[1];
        if (Math.abs(_tip.x - r.x) <= r.w / 2 && Math.abs(_tip.y - r.y) <= r.h / 2) { hit = entry; break; }
      }
      const z = _tip.z;
      if (hit && z < .05 && z > -.04) hovering = hit[0];
      if (hit && z < .01 && z > -.04) {
        if (!pokeDown.current) { pokeDown.current = true; if (!hit[1].disabled) pressOnce(hit[1].onPress); }
      } else if (z > .022 || !hit) pokeDown.current = false;
    }
    if (hovering !== hot) setHot(hovering);
  });

  if (!session || !canBuild) return null;

  // ---- Actions ----
  const store = () => useWorldStore.getState();
  const hold = (h: HeldItem) => { setGamesOpen(false); store().setActiveTool('select'); store().setPlacementShape(null); useVRBuild.getState().setHeld(h); };
  const selPrim = () => { const s = store().selectedObjectId; return s && s.startsWith('prim-') ? s : null; };
  const applyColor = (color: string) => {
    const sel = selPrim(); if (!sel) return setNotice('Select a shape first');
    if (store().primitives[sel]?.shape === 'gametable') return setNotice('Game tables have a fixed look');
    if (store().primitives[sel]?.shape === 'arcade') return setNotice('Arcade cabinets take their game’s colours');
    if (store().primitives[sel]?.shape === 'leaderboard') return setNotice('Change a leaderboard’s look in its panel on desktop');
    if (store().primitives[sel]?.shape === 'npc') return setNotice('Change an NPC in its panel on desktop');
    store().updatePrimitive(sel, { color, texturePreset: null, textureUrl: null, pbrMaps: null });
    if (worldId) broadcastObjectUpdate(worldId, 'primitive', sel, { color, texturePreset: null, textureUrl: null, pbrMaps: null });
  };
  const applyMaterial = (preset: string) => {
    const sel = selPrim(); if (!sel) return setNotice('Select a shape first');
    store().updatePrimitive(sel, { texturePreset: preset, textureUrl: null, pbrMaps: null });
    if (worldId) broadcastObjectUpdate(worldId, 'primitive', sel, { texturePreset: preset, textureUrl: null, pbrMaps: null });
  };
  const applyPbr = (a: MaterialAsset) => {
    const sel = selPrim(); if (!sel) return setNotice('Select a shape first');
    if (applying) return;
    setApplying(a.id);
    void applyAmbientCG(worldId, sel, a, tile)
      .then((ok) => setNotice(ok ? `${a.name} applied` : 'Couldn’t apply that one'))
      .catch(() => setNotice('Couldn’t apply that one'))
      .finally(() => setApplying(null));
  };
  const setTransform = (m: string) => {
    const s = store(); s.setActiveTool('select'); s.setPlacementShape(null);
    (window as any).__vrTransformMode = m; setMode(m);
    window.dispatchEvent(new CustomEvent('vr-transform-mode', { detail: m }));
  };
  const copyToHand = () => {
    const s = store(); const sel = s.selectedObjectId; if (!sel) return setNotice('Select something first');
    if (sel.startsWith('prim-') && s.primitives[sel]) { const t = s.primitives[sel]; hold({ kind: 'primitive', shape: t.shape, template: t, game: t.game, arcadeGame: t.arcadeGame, board: t.board }); }
    else if (sel.startsWith('light-') && s.lights[sel]) hold({ kind: 'light', template: s.lights[sel] });
    else if (s.contentItems[sel]) { const t = s.contentItems[sel] as any; hold({ kind: 'content', spec: { url: t.url, filename: t.filename }, template: t }); }
    else setNotice('That item can’t be copied here');
  };
  const remove = () => {
    const s = store(); const sel = s.selectedObjectId; if (!sel) return setNotice('Select something first');
    if (sel.startsWith('prim-')) { s.deletePrimitive(sel); if (worldId) broadcastObjectDelete(worldId, 'primitive', sel); }
    else if (sel.startsWith('light-')) { s.deleteLight(sel); if (worldId) broadcastObjectDelete(worldId, 'light', sel); }
    else if (s.contentItems[sel]) { s.deleteContentItem(sel); if (worldId) broadcastObjectDelete(worldId, 'content', sel); }
    else return setNotice('That item can’t be deleted here');
    s.setSelectedObjectId(null);
  };
  const placePanel = (type: 'screen-share' | 'browser') => { const s = store(); s.setVrPanelType(type); s.setActiveTool('place'); setOpen(false); };
  const save = () => {
    const fn = (window as any).__gathrxrSaveWorld as (() => Promise<void>) | undefined;
    if (!fn) return setNotice('Save isn’t available here');
    const before = store().lastSaved?.getTime() ?? 0;
    setNotice('Saving…');
    void fn().then(() => setNotice((store().lastSaved?.getTime() ?? 0) > before ? 'Saved' : 'Couldn’t save — try again'));
  };
  const setSpawnHere = () => {
    const o = originRef.current; if (!o) return;
    store().setSpawnPosition([+o.position.x.toFixed(2), +o.position.y.toFixed(2), +o.position.z.toFixed(2)]);
    setNotice('Spawn set here — Save to keep it');
  };
  const backToStart = () => { const o = originRef.current; if (!o) return; const sp = store().spawnPosition; o.position.set(sp[0], sp[1], sp[2]); };

  // ---- Layout (metres, palette-local; +y up, centre origin) ----
  const top = PH / 2;
  const tabsY = top - .021;
  const grid = (cols: number, rowH: number, gap = .006) => {
    const w = (PW - .02 - gap * (cols - 1)) / cols;
    return {
      w, h: rowH,
      x: (c: number) => -PW / 2 + .01 + w / 2 + c * (w + gap),
      y: (r: number) => tabsY - .03 - rowH / 2 - r * (rowH + gap),
    };
  };
  const sel = selectedId;
  const selName = sel ? (primitives[sel]?.name || store().lights[sel]?.name || (store().contentItems[sel] as any)?.filename || 'Selected item') : null;
  const tp = { reg, hot };

  let body: React.ReactNode;
  if (held) {
    const g4 = grid(2, .05);
    body = <>
      <Text raycast={noRaycast} position={[0, tabsY - .055, .004]} fontSize={.016} color={colors.teal} anchorX="center">{`Holding: ${heldLabel(held)}`}</Text>
      <Text raycast={noRaycast} position={[0, tabsY - .085, .004]} fontSize={.0095} color={colors.muted} maxWidth={PW - .03} textAlign="center" anchorX="center">
        {held.kind === 'primitive' && held.shape === 'portal' && !held.template
          ? 'Release to drop the entrance — then the exit, wherever it should lead'
          : 'Move your right hand, then pinch / pull the trigger to drop it there'}
      </Text>
      <Tile id="snap" {...tp} x={g4.x(0)} y={g4.y(2)} w={g4.w} h={g4.h} label={snapOn ? 'Snap: on' : 'Snap: off'} active={snapOn}
        onPress={() => store().setSnapSettings({ grid: !snapOn })} />
      <Tile id="cancel" {...tp} x={g4.x(1)} y={g4.y(2)} w={g4.w} h={g4.h} label="Cancel" danger
        onPress={() => useVRBuild.getState().setHeld(null)} />
    </>;
  } else if (tab === 'shapes' && gamesOpen) {
    const games = gameList();
    // Table games, then arcade cabinets and the leaderboard; more columns as the list grows.
    const n = games.length + arcade.games.length + (arcade.ready ? 1 : 0) + 1; // + Speaker
    const cols = n > 12 ? 4 : n > 8 ? 3 : 2; // four rows fit above Back
    const g = grid(cols, .036, .005);
    body = <>
      {games.map((gm, i) => <Tile key={gm.kind} id={`game-${gm.kind}`} {...tp} x={g.x(i % cols)} y={g.y(Math.floor(i / cols))} w={g.w} h={g.h}
        label={`${gm.glyph} ${gm.label}`} hint={`${gm.minPlayers === gm.maxPlayers ? gm.minPlayers : `${gm.minPlayers}–${gm.maxPlayers}`} players`}
        onPress={() => { armGamePlacement(gm.kind); hold({ kind: 'primitive', shape: 'gametable', game: gm.kind }); }} />)}
      {arcade.games.map((ag, j) => {
        const i = games.length + j;
        return <Tile key={`arcade-${ag.id}`} id={`arcade-${ag.id}`} {...tp} x={g.x(i % cols)} y={g.y(Math.floor(i / cols))} w={g.w} h={g.h}
          label={ag.title} hint="Arcade" onPress={() => { armArcadePlacement(ag.id); hold({ kind: 'primitive', shape: 'arcade', arcadeGame: ag.id }); }} />;
      })}
      {arcade.ready && (() => {
        const i = games.length + arcade.games.length;
        return <Tile id="arcade-leaderboard" {...tp} x={g.x(i % cols)} y={g.y(Math.floor(i / cols))} w={g.w} h={g.h}
          label="Leaderboard" hint="Top scores" onPress={() => { armBoardPlacement(); hold({ kind: 'primitive', shape: 'leaderboard', board: { ...DEFAULT_BOARD } }); }} />;
      })()}
      {(() => {
        const i = games.length + arcade.games.length + (arcade.ready ? 1 : 0);
        return <Tile id="audio-speaker" {...tp} x={g.x(i % cols)} y={g.y(Math.floor(i / cols))} w={g.w} h={g.h}
          label="Speaker" hint="Music, positional" onPress={() => hold({ kind: 'primitive', shape: 'speaker' })} />;
      })()}
      <Tile id="games-back" {...tp} x={0} y={-top + .02} w={PW - .02} h={.026} label="‹ Back" onPress={() => setGamesOpen(false)} />
    </>;
  } else if (tab === 'shapes') {
    const g = grid(4, .052);
    body = <>
      <Tile id="library" {...tp} x={0} y={g.y(0) + .008} w={PW - .02} h={.036} label="Library & Studio" hint="Models — drop them from your hand" primary
        onPress={() => { useVRBuild.getState().setLibraryOpen(true); setOpen(false); }} />
      {SHAPES.map((s, i) => <Tile key={s.value} id={`shape-${s.value}`} {...tp} x={g.x(i % 4)} y={g.y(1 + Math.floor(i / 4)) + .02} w={g.w} h={g.h}
        label={s.label} glyph={s.value} onPress={() => hold({ kind: 'primitive', shape: s.value })} />)}
      <Tile id="games" {...tp} x={g.x(3)} y={g.y(2) + .02} w={g.w} h={g.h} label="Games" hint="Tables · speaker" onPress={() => setGamesOpen(true)} />
    </>;
  } else if (tab === 'edit') {
    const none = !sel;
    const g = grid(3, .04);
    const y0 = tabsY - .055;
    body = <>
      <Text raycast={noRaycast} position={[-PW / 2 + .012, tabsY - .036, .004]} anchorX="left" fontSize={.0105} color={none ? colors.muted : colors.teal} maxWidth={PW - .03}>
        {none ? 'Point at something and pinch / trigger to select it' : `Selected: ${String(selName).slice(0, 34)}`}
      </Text>
      {(['Move', 'Turn', 'Size'] as const).map((l, i) => {
        const m = ['translate', 'rotate', 'scale-uniform'][i];
        return <Tile key={l} id={`mode-${m}`} {...tp} x={g.x(i)} y={y0 - .02} w={g.w} h={.034} label={l} active={mode === m} disabled={none} onPress={() => setTransform(m)} />;
      })}
      <Tile id="copy" {...tp} x={g.x(0)} y={y0 - .062} w={g.w} h={.034} label="Copy" hint="into your hand" disabled={none} onPress={copyToHand} />
      <Tile id="delete" {...tp} x={g.x(1)} y={y0 - .062} w={g.w} h={.034} label="Delete" danger disabled={none} onPress={remove} />
      <Tile id="snap" {...tp} x={g.x(2)} y={y0 - .062} w={g.w} h={.034} label={snapOn ? 'Snap: on' : 'Snap: off'} active={snapOn}
        onPress={() => store().setSnapSettings({ grid: !snapOn })} />
      <Tile id="undo" {...tp} x={-PW / 4 + .003} y={y0 - .104} w={PW / 2 - .016} h={.034} label="↶ Undo" onPress={() => { if (!vrUndo(worldId)) setNotice('Nothing to undo'); }} />
      <Tile id="redo" {...tp} x={PW / 4 - .003} y={y0 - .104} w={PW / 2 - .016} h={.034} label="Redo ↷" onPress={() => { if (!vrRedo(worldId)) setNotice('Nothing to redo'); }} />
      <Text raycast={noRaycast} position={[0, -top + .016, .004]} fontSize={.0085} color={colors.muted} maxWidth={PW - .03} textAlign="center" anchorX="center">
        {mode === 'rotate' ? 'Pinch or grip an object and drag round it to turn it' : mode === 'scale-uniform' ? 'Pinch or grip an object and drag out / in to resize it' : 'Pinch or grip an object to carry it · twist your wrist to turn it'} · both hands on it: move, turn + resize
      </Text>
    </>;
  } else if (tab === 'colour') {
    const none = !selPrim();
    const gc = grid(6, .032, .005);
    const gm = grid(4, .028, .005);
    body = <>
      {COLORS.map((c, i) => <Tile key={c} id={`colour-${i}`} {...tp} x={gc.x(i % 6)} y={gc.y(Math.floor(i / 6)) + .002} w={gc.w} h={gc.h}
        swatch={c} disabled={none} onPress={() => applyColor(c)} />)}
      {MATERIALS.map(([p, l], i) => <Tile key={p} id={`mat-${p}`} {...tp} x={gm.x(i % 4)} y={gm.y(Math.floor(i / 4)) - .072} w={gm.w} h={gm.h}
        label={l} disabled={none} onPress={() => applyMaterial(p)} />)}
      <Tile id="more-materials" {...tp} x={0} y={-top + .02} w={PW - .02} h={.026} label={matsOpen ? 'Hide materials' : 'More materials…'} active={matsOpen} disabled={none}
        onPress={() => setMatsOpen((v) => !v)} />
      {none && <Text raycast={noRaycast} position={[0, -top + .045, .004]} fontSize={.0085} color={colors.muted} anchorX="center">Select a shape to colour it</Text>}
    </>;
  } else {
    const g = grid(2, .042);
    body = <>
      <Tile id="save" {...tp} x={g.x(0)} y={g.y(0)} w={g.w} h={g.h} label="Save world" hint="Keep your changes" primary onPress={save} />
      <Tile id="spawn" {...tp} x={g.x(1)} y={g.y(0)} w={g.w} h={g.h} label="Spawn here" hint="Where visitors arrive" onPress={setSpawnHere} />
      <Tile id="light" {...tp} x={g.x(0)} y={g.y(1)} w={g.w} h={g.h} label="Light" hint="Into your hand" onPress={() => hold({ kind: 'light' })} />
      <Tile id="start" {...tp} x={g.x(1)} y={g.y(1)} w={g.w} h={g.h} label="Back to start" hint="Jump to spawn" onPress={() => { setOpen(false); backToStart(); }} />
      <Tile id="screen" {...tp} x={g.x(0)} y={g.y(2)} w={g.w} h={g.h} label="Screen" hint="Share / video" onPress={() => placePanel('screen-share')} />
      <Tile id="browser" {...tp} x={g.x(1)} y={g.y(2)} w={g.w} h={g.h} label="Web panel" hint="Browser in world" onPress={() => placePanel('browser')} />
      <Tile id="exit" {...tp} x={0} y={-top + .02} w={PW - .02} h={.026} label="Exit VR" danger onPress={() => { setOpen(false); void session.end().catch(() => {}); }} />
    </>;
  }

  return <group ref={groupRef} visible={open} name="vr-build-palette">
    {open && <>
      {/* Backing soaks up presses (and marks this as UI, so a pinch here never teleports). */}
      <group onPointerOver={stop} onPointerDown={stop} onClick={stop}>
        <Surface width={PW + .006} height={PH + .006} color={colors.border} z={-.003} r={.016} />
        <Surface width={PW} height={PH} color={colors.panel} r={.014} />
      </group>
      {!held && (['shapes', 'edit', 'colour', 'world'] as Tab[]).map((t2, i) => {
        // four tabs, then the close button, across the top row
        const w = (PW - .02 - .026 - .004 * 4) / 4;
        const x = -PW / 2 + .01 + w / 2 + i * (w + .004);
        return <Tile key={t2} id={`tab-${t2}`} {...tp} x={x} y={tabsY} w={w} h={.026}
          label={t2 === 'colour' ? 'Colour' : t2.charAt(0).toUpperCase() + t2.slice(1)} active={tab === t2}
          onPress={() => { setTab(t2); setGamesOpen(false); }} />;
      })}
      {!held && <Tile id="close" {...tp} x={PW / 2 - .01 - .013} y={tabsY} w={.026} h={.026} label="✕" onPress={() => setOpen(false)} />}
      {body}
      {matsOpen && !held && (() => {
        // Materials panel: stacked on top of the palette, same width, moves with it.
        const by = top + .012 + MAT_H / 2;
        const gw = (PW - .02 - .005 * (MAT_COLS - 1)) / MAT_COLS;
        const gh = .068;
        const gx = (c: number) => -PW / 2 + .01 + gw / 2 + c * (gw + .005);
        const gy = (r: number) => by + MAT_H / 2 - .036 - gh / 2 - r * (gh + .005);
        const fy = by - MAT_H / 2 + .02;
        return <group>
          <group position={[0, by, 0]} onPointerOver={stop} onPointerDown={stop} onClick={stop}>
            <Surface width={PW + .006} height={MAT_H + .006} color={colors.border} z={-.003} r={.016} />
            <Surface width={PW} height={MAT_H} color={colors.panel} r={.014} />
          </group>
          <Text raycast={noRaycast} position={[-PW / 2 + .012, by + MAT_H / 2 - .018, .004]} anchorX="left" fontSize={.012} color={colors.text}>Materials</Text>
          <Text raycast={noRaycast} position={[PW / 2 - .045, by + MAT_H / 2 - .018, .004]} anchorX="right" fontSize={.0085} color={colors.muted}>{`${mats.page + 1} / ${mats.pages}`}</Text>
          <Tile id="mats-close" {...tp} x={PW / 2 - .023} y={by + MAT_H / 2 - .018} w={.024} h={.022} label="✕" onPress={() => setMatsOpen(false)} />
          {mats.loading && <Text raycast={noRaycast} position={[0, by, .004]} fontSize={.011} color={colors.muted} anchorX="center">Loading materials…</Text>}
          {mats.error && <Text raycast={noRaycast} position={[0, by, .004]} fontSize={.01} color="#ffb1ad" anchorX="center" maxWidth={PW - .04}>{mats.error}</Text>}
          {!mats.loading && !mats.error && mats.assets.slice(0, MAT_COLS * MAT_ROWS).map((a, i) =>
            <ThumbTile key={a.id} id={`mat-pbr-${i}`} reg={reg} hot={hot} x={gx(i % MAT_COLS)} y={gy(Math.floor(i / MAT_COLS))} w={gw} h={gh}
              url={a.thumbnailUrl} label={a.name} busy={applying === a.id} onPress={() => applyPbr(a)} />)}
          <Tile id="mats-prev" {...tp} x={-PW / 2 + .03} y={fy} w={.04} h={.022} label="‹" disabled={mats.page === 0} onPress={() => mats.setPage(Math.max(0, mats.page - 1))} />
          {[1, 2, 4, 8, 16].map((n, i) => <Tile key={n} id={`tile-${n}`} {...tp} x={-.075 + i * .037} y={fy} w={.033} h={.022}
            label={`${n}×`} active={tile === n} onPress={() => setTile(n)} />)}
          <Tile id="mats-next" {...tp} x={PW / 2 - .03} y={fy} w={.04} h={.022} label="›" disabled={mats.page + 1 >= mats.pages}
            onPress={() => mats.setPage(Math.min(mats.pages - 1, mats.page + 1))} />
        </group>;
      })()}
      {notice && <group position={[0, -top - .02, 0]}>
        <Surface width={PW * .8} height={.026} color={colors.panel} r={.01} />
        <Text raycast={noRaycast} position={[0, 0, .004]} fontSize={.011} color={colors.teal} anchorX="center">{notice}</Text>
      </group>}
    </>}
  </group>;
}
